import { type UserPreferenceAttributes } from './generated/UserPreferenceAttributes';
import ApplicationModel, { attr } from '../ApplicationModel';
import { model } from '../modelBuilder';
import * as Sentry from '@sentry/browser';
import { csrfFetch } from 'javascripts/csrf_token';
import {
  getLocalStoragePreference,
  setLocalStoragePreference,
} from 'javascripts/utils';

interface UserPreference extends UserPreferenceAttributes {}

type UserPreferenceOptions = {
  /** Whether to cache the value in local storage when setting,
   * or check local storage first when getting */
  cache: boolean;
};

@model
class UserPreference extends ApplicationModel {
  // Start auto-generated
  static typename: 'UserPreference' = 'UserPreference';
  get typename(): 'UserPreference' {
    return 'UserPreference';
  }

  static internalTypename: 'UserPreference' = 'UserPreference';
  get internalTypename(): 'UserPreference' {
    return 'UserPreference';
  }

  static filterType = 'UserPreferenceFilters!';
  static filters = { name: '[String!]!' };

  static fields = {
    name: attr({ readable: true }),
    value: attr({ readable: true, json: true }),
  };
  // End auto-generated

  /**
   * @param {string} preference
   * @param {UserPreferenceOptions} options
   */
  static async getUserPreferenceValue(
    preference,
    options: UserPreferenceOptions = { cache: false }
  ) {
    if (options.cache) {
      const cachedValue = getLocalStoragePreference(preference);

      if (cachedValue !== undefined && cachedValue !== null) return cachedValue;
    }

    const userPreference = await UserPreference.select('value').findBy({
      name: preference,
    });

    if (options.cache && userPreference?.value !== undefined) {
      setLocalStoragePreference(preference, userPreference.value);
    }

    return userPreference?.value;
  }

  /**
   * @param {string} preference
   * @param {string|boolean} preference_value
   * @param {UserPreferenceOptions} options
   */
  static async setUserPreference(
    preference,
    preference_value,
    options: UserPreferenceOptions = { cache: false }
  ) {
    try {
      const response = await csrfFetch('/user_preferences', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: JSON.stringify({
          reactive_client_id: window.reactiveClientId(),
          preference,
          preference_value,
        }),
      });

      if (options.cache && response.ok) {
        setLocalStoragePreference(preference, preference_value);
      }

      return response.ok;
    } catch (error) {
      console.error('Failed to setUserPreference:', error);
      Sentry.captureException(error);
      return false;
    }
  }

  /**
   * @param {string} preference
   */
  static async deleteUserPreference(preference) {
    const response = await csrfFetch('/user_preferences', {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
      },
      body: JSON.stringify({
        reactive_client_id: window.reactiveClientId(),
        preference,
      }),
    });

    return response.ok;
  }
}

export default UserPreference;
